import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Shield, Award, Phone } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import { SpeakVideo } from "@/components/SpeakVideo";
import { SEO, localBusinessJsonLd } from "@/components/SEO";
import { CLEARBOOK_BOOKING_URL } from "@/lib/clearbook";

const SERVICES = [
  {
    label: "Rottebekæmpelse",
    to: "/ydelser/rottebekaempelse",
    image: ASSETS.images.rats,
    blurb:
      "Certificeret rottebekæmpelse med lokalisering, sikring og dokumentation i både kloak og bygning.",
  },
  {
    label: "Kloakopgaver",
    to: "/ydelser/kloakopgaver",
    image: ASSETS.images.pipes,
    blurb:
      "Autoriseret kloakarbejde fra akutte afløbsproblemer til større renoveringer og anlægsopgaver.",
  },
  {
    label: "Serviceaftale",
    to: "/ydelser/serviceaftale",
    image: ASSETS.images.contract,
    blurb:
      "Forebyggende service af kloak og afløb, så problemer opdages i tide og driften holdes stabil.",
  },
  {
    label: "No-dig",
    to: "/ydelser/no-dig",
    image: ASSETS.images.noDig,
    blurb:
      "Strømpeforing og punktreparationer, hvor vi renoverer rørene uden unødvendig opgravning.",
  },
  {
    label: "Spul & Rens",
    to: "/ydelser/spul-rens",
    image: ASSETS.images.flush,
    blurb:
      "Højtryksspuling, rensning og TV-inspektion, når afløb stopper til eller skal dokumenteres.",
  },
];

const SEGMENTS = [
  {
    label: "Forsikringsselskaber",
    to: "/forsikring",
    blurb: "Hurtig respons, klar dokumentation og en samarbejdspartner, der forstår ansvaret i skadeshåndteringen.",
  },
  {
    label: "Hovedentreprenører",
    to: "/hovedentreprenoerer",
    blurb: "Faglig sparring, præcis planlægning og kompromisløs udførelse fra første dialog til færdigt resultat.",
  },
  {
    label: "Forsyninger",
    to: "/forsyninger",
    blurb: "Driftssikre løsninger, gennemsigtig proces og dokumentation, der kan stå på mål overfor både drift og ledelse.",
  },
];

export const Home = () => {
  return (
    <>
      <SEO
        title="Autoriseret kloakmester i Farum & Nordsjælland"
        description="Farum Kloak – autoriseret kloakmester og certificeret rottebekæmper. Døgnvagt, kloakservice, no-dig, TV-inspektion og rottebekæmpelse i Nordsjælland og Storkøbenhavn."
        jsonLd={localBusinessJsonLd}
      />
      {/* HERO with loop video */}
      <section
        data-testid="home-hero"
        className="hero-loop relative min-h-screen flex flex-col justify-end overflow-hidden"
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          disableRemotePlayback
          className="absolute inset-0 w-full h-full object-cover"
          poster={ASSETS.images.teamGroup}
        >
          {/* iOS Safari reads sources in order and fails silently on the first
              format it thinks it can play. MP4/H.264 must be first so iPhones
              always get a working stream. WebM is kept as a smaller fallback
              for browsers that can use it. */}
          <source src={ASSETS.videos.loopHero} type="video/mp4" />
          <source src="/media/loop-hero.webm" type="video/webm" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/90" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-12 md:pb-16 w-full">
          <div className="eyebrow mb-6 fk-rise">Autoriseret kloakmester · Certificeret rottebekæmper</div>
          <h1 className="font-display text-5xl sm:text-6xl md:text-8xl leading-[0.95] tracking-tight max-w-5xl fk-rise-2">
            Et elitehold til
            <br />
            <em className="text-[color:var(--fk-gold)]">kloakopgaver, der kræver præcision.</em>
          </h1>
          <p className="mt-8 max-w-xl text-lg md:text-xl text-white/80 font-light fk-rise-3">
            Hos Farum Kloak kombinerer vi erfaring, præcision og kompromisløs
            udførelse. Vi står på aftalerne, holder dialogen åben og tager
            ejerskab fra planlægning til færdigt resultat.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 fk-rise-4">
            <a
              data-testid="hero-call"
              href={ASSETS.brand.phoneTel}
              className="px-7 py-4 text-[11px] tracking-[0.24em] uppercase bg-[color:var(--fk-gold)] text-black hover:bg-[color:var(--fk-gold-hi)] transition-colors flex items-center justify-center gap-3"
            >
              <Phone className="w-4 h-4" />
              Døgnvagt · {ASSETS.brand.phone}
            </a>
            <a
              data-testid="hero-booking"
              href={CLEARBOOK_BOOKING_URL}
              className="px-7 py-4 text-[11px] tracking-[0.24em] uppercase border border-white/30 hover:border-[color:var(--fk-gold)] hover:text-[color:var(--fk-gold)] transition-colors text-center"
            >
              Book opgave
            </a>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-white/10 bg-black/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid grid-cols-2 md:grid-cols-4 gap-10">
          {[
            { Icon: Award, top: "10+ år", btm: "Erfaring" },
            { Icon: Clock, top: "Døgnvagt", btm: "24/7 respons" },
            { Icon: Shield, top: "Aut.", btm: "Kloakmester" },
            { Icon: Shield, top: "Cert.", btm: "Rottefænger" },
          ].map(({ Icon, top, btm }, i) => (
            <div key={i} className="flex items-center gap-4">
              <Icon className="w-6 h-6 text-[color:var(--fk-gold)]" />
              <div>
                <div className="font-display text-2xl">{top}</div>
                <div className="text-xs tracking-[0.18em] uppercase text-white/60">
                  {btm}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Manifesto */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="eyebrow mb-6">Manifest</div>
          </div>
          <div className="md:col-span-8">
            <p className="font-display text-3xl md:text-5xl leading-[1.15] tracking-tight">
              <em className="text-[color:var(--fk-gold)]">Driftssikkerhed</em>,
              præcision og ansvar kan ikke forhandles.
            </p>
            <p className="mt-8 text-white/60 font-light max-w-2xl">
              Hos Farum Kloak arbejder vi dér, hvor opgaven kræver mere end
              udførelse. Vi planlægger med præcision, holder dialogen åben og
              tager ejerskab hele vejen fra første kontakt til færdigt resultat.
            </p>
            <p className="mt-6 text-[color:var(--fk-gold)] font-light text-sm tracking-[0.14em] uppercase">
              Hurtig respons. Klare svar. Ingen bullshit.
            </p>
          </div>
        </div>
      </section>

      {/* For hvem — moved up per client feedback */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="home-segments">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-5">
            <div className="eyebrow mb-4">For hvem</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              Vi løfter kloakopgaver <em className="text-[color:var(--fk-gold)]">for professionelle partnere.</em>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-white/60 font-light">
              Forsikringsselskaber, hovedentreprenører og forsyninger vælger
              Farum Kloak, når opgaven kræver hurtig respons, åben dialog og et
              team, der tager ansvar hele vejen.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
          {SEGMENTS.map((seg, i) => (
            <Link
              key={seg.to}
              to={seg.to}
              data-testid={`segment-card-${i}`}
              className="group bg-[color:var(--fk-bg)] p-10 md:p-12 hover:bg-[color:var(--fk-bg-2)] transition-colors"
            >
              <div className="eyebrow mb-8">0{i + 1}</div>
              <h3 className="font-display text-3xl mb-4 leading-tight">
                {seg.label}
              </h3>
              <p className="text-white/60 font-light mb-10">{seg.blurb}</p>
              <span className="inline-flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-[color:var(--fk-gold)] group-hover:gap-5 transition-all">
                Læs mere
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* I felten — editorial gallery (replaces unclear grabs strip) */}
      <section
        data-testid="home-field-gallery"
        className="border-t border-white/10 bg-[color:var(--fk-bg-2)]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-0">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-5">
              <div className="eyebrow mb-4">I marken</div>
              <h2 className="font-display text-4xl md:text-5xl leading-tight">
                Sådan ser <em className="text-[color:var(--fk-gold)]">ansvar ud i praksis.</em>
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-white/60 font-light">
                TV-inspektion, opgravning, strømpeforing og alt det, der ligger
                imellem. Vi møder opgaven med præcision, holder dialogen åben og
                tager ejerskab, indtil løsningen er på plads.
              </p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3">
          {[
            ASSETS.grabs.overheadTrench,
            ASSETS.grabs.tvInspectionField,
            ASSETS.grabs.carryingPipe,
            ASSETS.grabs.excavatorTrack,
            ASSETS.grabs.toolCloseup,
            ASSETS.grabs.pipeDetail,
          ].map((src, i) => (
            <div
              key={i}
              className="relative aspect-square overflow-hidden group"
            >
              <img
                src={src}
                alt="Farum Kloak i marken"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
              />
            </div>
          ))}
        </div>
      </section>

      {/* Speak Farum video */}
      <section
        data-testid="home-speak-video"
        className="border-y border-white/10 bg-[color:var(--fk-bg-2)]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
            <div className="md:col-span-5">
              <div className="eyebrow mb-4">Mød Farum Kloak</div>
              <h2 className="font-display text-4xl md:text-5xl leading-tight">
                Folkene bag <em className="text-[color:var(--fk-gold)]">fagligheden.</em>
              </h2>
            </div>
            <div className="md:col-span-7 flex md:justify-end items-end">
              <p className="text-white/60 font-light max-w-md">
                Et kort indblik i holdet, hverdagen og den mentalitet, der præger
                Farum Kloak. Her handler det om at møde ordentligt op, finde
                løsningen og hjælpe hinanden, indtil opgaven er løst.
              </p>
            </div>
          </div>
          <SpeakVideo poster={ASSETS.grabs.portraitConversation} testIdSuffix="home" />
        </div>
      </section>

      {/* Services bento */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-5">
            <div className="eyebrow mb-4">Ydelser</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              Hele kompetencebredden <em className="text-[color:var(--fk-gold)]">samlet ét sted.</em>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-white/60 font-light">
              Fra akutte skader til planlagte kloakprojekter. Vi har udstyret,
              erfaringen og holdet til at løse opgaven effektivt og dokumentere
              arbejdet ordentligt.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {SERVICES.map((s, i) => (
            <Link
              key={s.to}
              to={s.to}
              data-testid={`service-card-${i}`}
              className={`group relative overflow-hidden border border-white/10 hover:border-[color:var(--fk-gold)]/40 transition-all duration-700 ${
                i === 0 ? "md:col-span-4 md:row-span-2 aspect-square md:aspect-auto md:min-h-[520px]" : "md:col-span-2 aspect-[4/3]"
              }`}
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-105"
                style={{ backgroundImage: `url(${s.image})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-6 md:p-8">
                <div className="flex items-center justify-between mb-3">
                  <div className="eyebrow">0{i + 1}</div>
                  <ArrowUpRight className="w-4 h-4 text-white/60 group-hover:text-[color:var(--fk-gold)] group-hover:rotate-12 transition-all" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl leading-tight">
                  {s.label}
                </h3>
                <p className="mt-3 text-sm text-white/70 font-light max-w-sm">
                  {s.blurb}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Team image */}
      <section
        data-testid="home-team-section"
        className="relative border-y border-white/10"
      >
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="aspect-square md:aspect-auto relative overflow-hidden">
            <img
              src={ASSETS.grabs.teamAtBase}
              alt="Farum Kloak holdet samlet ved hjemmebasen"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="bg-[color:var(--fk-bg-2)] p-10 md:p-20 flex flex-col justify-center">
            <div className="eyebrow mb-6">Holdet</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              Erfaring, der kan mærkes <em className="text-[color:var(--fk-gold)]">i udførelsen.</em>
            </h2>
            <p className="mt-6 text-white/70 font-light max-w-md">
              Hos Farum Kloak møder du et fasttømret hold med faglig stolthed,
              stærk intern sparring og viljen til at finde løsningen. Vi hjælper
              hinanden i marken, så opgaven bliver løst ordentligt første gang.
            </p>
            <Link
              to="/om-os"
              data-testid="home-team-link"
              className="mt-10 inline-flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-[color:var(--fk-gold)] hover:text-[color:var(--fk-gold-hi)]"
            >
              Læs om holdet
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Van feature — iconic brand shot */}
      <section
        data-testid="home-van-feature"
        className="relative border-t border-white/10"
      >
        <div
          className="relative h-[70vh] md:h-[85vh] bg-cover bg-center"
          style={{ backgroundImage: `url(${ASSETS.grabs.vanBranded})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-transparent" />
          <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex items-center">
            <div className="max-w-lg">
              <div className="eyebrow mb-6">Døgnvagt på vej</div>
              <h2 className="font-display text-4xl md:text-6xl leading-[1.05]">
                Når det haster, <em className="text-[color:var(--fk-gold)]">rykker vi ud.</em>
              </h2>
              <p className="mt-6 text-white/75 font-light">
                Akutte stop, rotteproblemer, TV-inspektion og kloakskader kræver
                hurtig handling. Vores biler er klar, udstyret er med, og vi går
                direkte efter at finde årsagen.
              </p>
              <a
                href={ASSETS.brand.phoneTel}
                data-testid="van-cta-call"
                className="mt-10 inline-block px-7 py-4 text-[11px] tracking-[0.24em] uppercase bg-[color:var(--fk-gold)] text-black hover:bg-[color:var(--fk-gold-hi)] transition-colors"
              >
                Ring {ASSETS.brand.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* USP / Quote section with gold accent */}
      <section
        className="relative border-y border-white/10 overflow-hidden bg-black"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-40 text-center">
          <div className="eyebrow mb-8 inline-block">Værdier</div>
          <blockquote className="font-display text-3xl md:text-5xl leading-[1.2] italic text-white/95">
            “Vi står på aftalerne og holder dialogen åben — og justerer, når
            virkeligheden kræver det.”
          </blockquote>
          <div className="mt-10 text-xs tracking-[0.24em] uppercase text-[color:var(--fk-gold)]">
            — Farum Kloak
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-7">
            <div className="eyebrow mb-6">Næste skridt</div>
            <h2 className="font-display text-5xl md:text-7xl leading-[1.02]">
              Skal vi tage <em className="text-[color:var(--fk-gold)]">dialogen?</em>
            </h2>
          </div>
          <div className="md:col-span-5 flex flex-col gap-4">
            <a
              href={ASSETS.brand.phoneTel}
              className="px-7 py-5 text-[11px] tracking-[0.24em] uppercase bg-[color:var(--fk-gold)] text-black hover:bg-[color:var(--fk-gold-hi)] transition-colors text-center"
            >
              Ring {ASSETS.brand.phone}
            </a>
            <a
              href={CLEARBOOK_BOOKING_URL}
              className="px-7 py-5 text-[11px] tracking-[0.24em] uppercase border border-white/20 hover:border-[color:var(--fk-gold)] hover:text-[color:var(--fk-gold)] transition-colors text-center"
            >
              Book online
            </a>
          </div>
        </div>
      </section>
    </>
  );
};
